'use client'

import React, { useCallback, useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import {
  FaFilm,
  FaSave,
  FaGlobeAmericas,
  FaClipboardList,
  FaTrash,
  FaLock,
  FaAngleDoubleRight,
  FaUserPlus,
  FaRegClock,
  FaMarker,
  FaThumbsUp,
  FaThumbsDown,
  FaMagic,
  FaRedo,
} from 'react-icons/fa'
import { BsChatDotsFill } from 'react-icons/bs'
import ClipLoader from 'react-spinners/ClipLoader'
import OverlayActionsMenu, {
  OVERLAY_PILL,
  OVERLAY_ICON,
} from './OverlayActionsMenu'
import ImageLightbox from './ImageLightbox'
import Modal from './Modal'
import { fakeProgressPct, FAKE_PROGRESS_CEILING } from '../helpers/fakeProgress'
import { VIDEO_V4, supportsExtend } from '../helpers/videoVersions'
import { foldClipChains } from '../lib/clip-chains'
import { offersPublish } from '../lib/api/media-ids.ts'
import ClipChainCard from './clips/ClipChainCard'

// HEIGHT-driven: capped at `--feed-media-max` (set by CreateSimplified; 70dvh is
// the SSR fallback), width derives from the image's aspect ratio so a `w-fit`
// wrapper shrinks to the artwork. `object-contain` guards box/intrinsic mismatch.
const FIT =
  'block h-auto w-auto max-w-full object-contain rounded-2xl max-h-[var(--feed-media-max,70dvh)]'

// Offscreen cards skip layout + paint to keep a long feed smooth; `auto` remembers
// each card's measured size so scrollbar/snap positions stay stable.
const CV_STYLE = {
  contentVisibility: 'auto',
  containIntrinsicSize: 'auto var(--feed-media-max, 70dvh)',
}

// Rounded "time left" label; null once elapsed.
const formatRemaining = (ms) => {
  if (ms == null || ms <= 0) return null
  const mins = Math.floor(ms / 60000)
  if (mins >= 60) return `${Math.floor(mins / 60)}h`
  if (mins >= 1) return `${mins}m`
  return '<1m'
}

const cdn = (url) =>
  (url || '').replace(
    process.env.NEXT_PUBLIC_URL_1,
    process.env.NEXT_PUBLIC_URL_2,
  )

// Defers src until in view so hundreds of <video>s don't fetch/decode at once;
// plays on enter, pauses on exit. metadata loads only after src is set, giving an
// in-view video its true aspect ratio instead of the 300x150 default.
function FeedVideo({ src, className }) {
  const ref = useRef(null)
  const [load, setLoad] = useState(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setLoad(true)
          el.play?.().catch(() => {})
        } else {
          el.pause?.()
        }
      },
      { threshold: 0.4 },
    )
    io.observe(el)
    return () => io.disconnect()
  }, [])

  return (
    <video
      ref={ref}
      src={load ? cdn(src) : undefined}
      preload="metadata"
      className={className}
      loop
      muted
      playsInline
      controls
    />
  )
}

// Memoized (comparator below) so prepends and upstream prompt keystrokes don't
// re-render the whole stack. Owns its menu-open state to keep the overlay visible
// without lifting state to the parent (which would re-render every card).
const FeedItem = React.memo(
  function FeedItem({
    item,
    user,
    t,
    isDeleting,
    videoIsLoading,
    isChatting,
    isCharBusy,
    onAnimate,
    onClone,
    onTweak,
    onChat,
    onUseCharacter,
    onDownload,
    onPublish,
    onSaveWorkflow,
    onDelete,
    onExtend,
    onRedo,
    onOpenFull,
    countdown,
    onTimerClick,
    onThumb,
  }) {
    const [menuOpen, setMenuOpen] = useState(false)
    const blurClass =
      item.blurry && !user ? 'blur-2xl' : item.blurry ? 'blur-md' : ''
    const isVideo = item.type === 'video'
    // Every version is extendable now. The VIDEO_V4 default is kept for items cached before v5
    // shipped, which carry no version — harmless now that the answer no longer depends on it.
    // A video with its id only: without one, Extend has nothing to name as its source (12 October 2026).
    const canExtend =
      isVideo && !!item.docRef && supportsExtend(item.videoVersion || VIDEO_V4)
    // An extension made in this feed can be redone: its parent extended again with the same
    // choices. Unsaved only, like Extend — a saved card is one the user chose to keep.
    const canRedo =
      canExtend &&
      !!item.redoOf &&
      !item.savedStatus &&
      item.url?.includes('temp')

    // Saved/Published status, else the expiry countdown (published wins if both).
    const statusBadge =
      item.savedStatus === 'published'
        ? {
            icon: <FaGlobeAmericas className="text-[0.7rem]" />,
            label: t('published', 'Published'),
          }
        : item.savedStatus === 'saved'
          ? {
              icon: <FaSave className="text-[0.7rem]" />,
              label: t('saved', 'Saved'),
            }
          : countdown
            ? {
                icon: <FaRegClock className="text-[0.7rem]" />,
                label: countdown,
                isTimer: true,
              }
            : null

    return (
      <div
        className="relative mx-auto group w-fit max-w-[min(680px,100%)] lg:max-w-[min(880px,100%)]"
        style={CV_STYLE}
      >
        {!item.blurry && statusBadge && (
          <div
            onClick={
              statusBadge.isTimer
                ? () =>
                    onTimerClick?.(statusBadge.label, isVideo, item.earlyAccess)
                : undefined
            }
            className={`absolute top-3 left-3 z-20 inline-flex select-none items-center gap-1.5 rounded-full border border-white/20 bg-black/60 px-2.5 py-1 text-[0.65rem] font-semibold text-white transition-opacity duration-200 opacity-100 lg:opacity-0 lg:group-hover:opacity-100 ${
              statusBadge.isTimer
                ? 'pointer-events-auto cursor-pointer'
                : 'pointer-events-none cursor-default'
            }`}
          >
            {statusBadge.icon}
            <span className="whitespace-nowrap">{statusBadge.label}</span>
          </div>
        )}
        {!item.blurry && (
          // Thumbs on every output (backend myapp/output_feedback.py); a vote can be changed.
          // Same buttons and hover reveal as the card's other actions (always shown on phones,
          // like the status badge, since there is no hover there).
          <div className="absolute bottom-3 left-3 z-20 flex items-center gap-2 transition-opacity duration-200 opacity-100 lg:opacity-0 lg:group-hover:opacity-100">
            {['up', 'down'].map((value) => (
              <button
                key={value}
                type="button"
                title={
                  value === 'up'
                    ? t('thumbsUp', 'Good result')
                    : t('thumbsDown', 'Bad result')
                }
                aria-pressed={item.feedback === value}
                onClick={() => onThumb?.(item, value)}
                className={`${OVERLAY_ICON} ${
                  item.feedback === value ? 'text-[var(--main-bg-color)]' : ''
                }`}
              >
                {value === 'up' ? (
                  <FaThumbsUp className="text-base" />
                ) : (
                  <FaThumbsDown className="text-base" />
                )}
              </button>
            ))}
          </div>
        )}
        {item.isUrl ? (
          isVideo ? (
            <FeedVideo src={item.url} className={`${FIT} ${blurClass}`} />
          ) : (
            <img
              src={cdn(item.url)}
              alt={t('generatedAlt')}
              loading="lazy"
              decoding="async"
              // Don't reveal a paywalled (blurred) image full-screen unblurred.
              onClick={
                item.blurry ? undefined : () => onOpenFull?.(cdn(item.url))
              }
              className={`${FIT} ${blurClass} ${item.blurry ? '' : 'cursor-pointer'}`}
            />
          )
        ) : item.blurry ? (
          <Link href="/upgrade" className="relative block w-fit max-w-full">
            <div className="absolute inset-0 flex flex-col items-center justify-center z-10">
              <FaLock className="text-white text-4xl opacity-40" />
              <p className="text-white font-semibold text-lg mt-2 opacity-80">
                {t('upgradetoview')}
              </p>
            </div>
            <div className={`${!user ? 'blur-3xl' : 'blur-md'}`}>
              <img
                src={`data:image/jpeg;base64,${item.url}`}
                alt={t('generatedAlt')}
                loading="lazy"
                decoding="async"
                className={FIT}
              />
            </div>
          </Link>
        ) : (
          <img
            src={`data:image/jpeg;base64,${item.url}`}
            alt={t('generatedAlt')}
            loading="lazy"
            decoding="async"
            onClick={() => onOpenFull?.(`data:image/jpeg;base64,${item.url}`)}
            className={`${FIT} cursor-pointer`}
          />
        )}

        {/* Actions reveal on hover, kept visible while the menu is open */}
        {!item.blurry && (
          <div
            className={`@container pointer-events-none absolute inset-0 z-20 flex items-start justify-end p-3 transition-opacity duration-200 ${
              menuOpen || isChatting || isCharBusy
                ? 'opacity-100'
                : 'opacity-0 group-hover:opacity-100'
            }`}
          >
            <div className="pointer-events-auto flex items-center gap-2">
              {isVideo ? (
                <>
                  {canRedo && (
                    <button
                      type="button"
                      disabled={videoIsLoading}
                      title={t(
                        'tryAgainExtendTitle',
                        'Retry this extend from the clip before',
                      )}
                      // Named redo_extend since launch (2026-10-06); kept so the series is unbroken.
                      data-track="generate.redo_extend"
                      className={OVERLAY_PILL}
                      onClick={() => onRedo?.(item)}
                    >
                      <FaRedo className="text-sm" />
                      <span className="hidden @[380px]:inline">
                        {t('tryAgainExtend', 'Try again')}
                      </span>
                    </button>
                  )}
                  {canExtend && item.url?.includes('temp') && (
                    <button
                      type="button"
                      disabled={videoIsLoading}
                      title={t('extend')}
                      // With redo_extend: an Extend tap right after a Try again tap on the same
                      // visit is someone who read Try again as "extend this clip".
                      data-track="generate.extend"
                      className={`${OVERLAY_PILL} relative`}
                      onClick={() => onExtend?.(item)}
                    >
                      <FaAngleDoubleRight className="text-base" />
                      <span className="hidden @[380px]:inline">
                        {t('extend')}
                      </span>
                    </button>
                  )}
                </>
              ) : (
                <button
                  type="button"
                  title={t('animate')}
                  className={OVERLAY_PILL}
                  onClick={() => onAnimate?.(item)}
                >
                  <FaFilm className="text-base" />
                  <span className="hidden @[380px]:inline">{t('animate')}</span>
                </button>
              )}

              {/* Images promote Tweak to the overlay and keep Save in the menu;
                  videos have no tweak, so Save stays on the overlay for them. */}
              {isVideo ? (
                <button
                  type="button"
                  title={t('saveFile')}
                  className={OVERLAY_ICON}
                  onClick={() => onDownload?.(item)}
                >
                  <FaSave className="text-base" />
                </button>
              ) : (
                <button
                  type="button"
                  title={t('tweak', 'Tweak')}
                  className={OVERLAY_ICON}
                  onClick={() => onTweak?.(item)}
                >
                  <FaMarker className="text-base" />
                </button>
              )}

              <OverlayActionsMenu
                onOpenChange={setMenuOpen}
                items={[
                  ...(!isVideo && item.cloneData
                    ? [
                        {
                          key: 'clone',
                          icon: <FaMagic className="text-sm" />,
                          label: t('clone'),
                          onClick: () => onClone?.(item),
                        },
                      ]
                    : []),
                  ...(!isVideo
                    ? [
                        {
                          key: 'download',
                          icon: <FaSave className="text-sm" />,
                          label: t('saveFile'),
                          onClick: () => onDownload?.(item),
                        },
                      ]
                    : []),
                  ...(offersPublish(item)
                    ? [
                        {
                          key: 'publish',
                          icon: <FaGlobeAmericas className="text-sm" />,
                          label: t('publish'),
                          onClick: () => onPublish?.(item),
                        },
                      ]
                    : []),
                  ...(!isVideo
                    ? [
                        {
                          key: 'character',
                          icon: <FaUserPlus className="text-sm" />,
                          label: t('useAsAiCharacter'),
                          loading: isCharBusy,
                          onClick: () => onUseCharacter?.(item),
                        },
                        {
                          key: 'chat',
                          icon: <BsChatDotsFill className="text-sm" />,
                          label: t('chatWithThisImage'),
                          loading: isChatting,
                          onClick: () => onChat?.(item),
                        },
                        {
                          key: 'workflow',
                          icon: <FaClipboardList className="text-sm" />,
                          label: t('saveWorkflow'),
                          onClick: () => onSaveWorkflow?.(item),
                        },
                      ]
                    : []),
                  {
                    key: 'delete',
                    icon: <FaTrash className="text-sm" />,
                    label: t('delete'),
                    destructive: true,
                    loading: isDeleting,
                    onClick: () => onDelete?.(item),
                  },
                ]}
              />
            </div>
          </div>
        )}
      </div>
    )
  },
  // Callbacks are intentionally excluded: they're stable wrappers (see Create.jsx)
  // and `t` is stable per language, so only this card's own data triggers a render.
  (prev, next) =>
    prev.item === next.item &&
    prev.user === next.user &&
    prev.isDeleting === next.isDeleting &&
    prev.videoIsLoading === next.videoIsLoading &&
    prev.isChatting === next.isChatting &&
    prev.isCharBusy === next.isCharBusy &&
    // Only the ~once-a-minute countdown label change re-renders a video card;
    // images pass undefined===undefined and never re-render on the tick.
    prev.countdown === next.countdown,
)

// In-feed "animating…" placeholder for image-to-video. Shows queue position while
// queued; otherwise a bar at max(real videoProgress, an optimistic estimate that
// creeps toward 90%) so it always moves even when backend progress is sparse/jumpy.
function AnimatingCard({
  source,
  sourceIsUrl,
  videoProgress = 0,
  queueLength,
  expectedDuration = 200,
  startedAt,
  label,
  secondaryLabel,
  phaseSwitchSeconds,
  t,
}) {
  // When the card is anchored to a persisted start time, seed the bar from
  // elapsed time so returning to the page resumes instead of flashing 0.
  const [estimatedProgress, setEstimatedProgress] = useState(() => {
    if (!startedAt) return 0
    return fakeProgressPct((Date.now() - startedAt) / 1000, expectedDuration)
  })
  const fakeTimerRef = useRef(null)
  const startTimeRef = useRef(0)

  // Queued until a position is reported and real progress arrives; then the bar runs.
  const inQueue = !!queueLength && videoProgress < 1
  const isProcessing = !inQueue

  useEffect(() => {
    if (!isProcessing) {
      if (fakeTimerRef.current) {
        clearInterval(fakeTimerRef.current)
        fakeTimerRef.current = null
      }
      setEstimatedProgress(0)
      return
    }
    // Anchor to the persisted start time when provided, so navigating away and
    // back resumes the bar from real elapsed time instead of restarting at 0.
    const start = startedAt || Date.now()
    startTimeRef.current = start
    const computeBase = () =>
      fakeProgressPct((Date.now() - start) / 1000, expectedDuration)
    setEstimatedProgress((prev) => Math.max(prev, computeBase()))

    const UPDATE_INTERVAL = 500
    const JITTER_MAX = 1
    fakeTimerRef.current = window.setInterval(() => {
      const jitter = (Math.random() * 2 - 1) * JITTER_MAX
      const rawNext = Math.min(
        FAKE_PROGRESS_CEILING,
        Math.max(0, computeBase() + jitter),
      )
      setEstimatedProgress((prevPct) => Math.max(prevPct, rawNext))
    }, UPDATE_INTERVAL)

    return () => {
      if (fakeTimerRef.current) {
        clearInterval(fakeTimerRef.current)
        fakeTimerRef.current = null
      }
    }
  }, [isProcessing, expectedDuration, startedAt])

  // Two-phase label (AI Character v3 image loader): after phaseSwitchSeconds the
  // label flips from "Generating…" to "Applying AI Character…" on its own timer,
  // independent of when the result returns, so the apply phase shows mid-wait rather
  // than only at the very end.
  const [inSecondaryPhase, setInSecondaryPhase] = useState(() => {
    if (!secondaryLabel || !phaseSwitchSeconds) return false
    const start = startedAt || Date.now()
    return (Date.now() - start) / 1000 >= phaseSwitchSeconds
  })
  useEffect(() => {
    if (!secondaryLabel || !phaseSwitchSeconds || inSecondaryPhase) return
    const start = startedAt || Date.now()
    const remainingMs = phaseSwitchSeconds * 1000 - (Date.now() - start)
    if (remainingMs <= 0) {
      setInSecondaryPhase(true)
      return
    }
    const id = window.setTimeout(() => setInSecondaryPhase(true), remainingMs)
    return () => window.clearTimeout(id)
  }, [secondaryLabel, phaseSwitchSeconds, startedAt, inSecondaryPhase])

  const activeLabel =
    inSecondaryPhase && secondaryLabel
      ? secondaryLabel
      : label || t('animating')

  const barPct = Math.min(100, Math.max(videoProgress, estimatedProgress))

  return (
    <div className="relative mx-auto w-fit max-w-[min(680px,100%)] lg:max-w-[min(880px,100%)]">
      {source ? (
        <img
          src={sourceIsUrl ? cdn(source) : `data:image/jpeg;base64,${source}`}
          alt={t('animatingAlt')}
          className={`${FIT} opacity-30`}
        />
      ) : (
        // No source to mirror: a `w-fit` parent can't derive width from a `w-full`
        // child, so reserve a definite portrait box.
        <div
          className="w-[min(86vw,390px)] max-w-full rounded-2xl bg-[var(--middle-bg-color)]"
          style={{
            aspectRatio: '3 / 4',
            maxHeight: 'var(--feed-media-max, 70dvh)',
          }}
        />
      )}
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 rounded-2xl bg-black/50 px-8">
        <ClipLoader color="#ffffff" size={40} />
        {inQueue ? (
          <p className="text-gray-200 text-sm font-semibold">
            {t('queuePosition')} {queueLength}
          </p>
        ) : (
          <>
            <div className="text-white text-sm font-semibold">
              {activeLabel}
            </div>
            <div className="w-full max-w-[220px]">
              <div className="relative h-2 w-full overflow-hidden rounded-full bg-[var(--secondary-bg-color)]">
                <div
                  className="absolute left-0 top-0 h-2 rounded-full bg-[var(--main-bg-color)] transition-[width] duration-1000 ease-in-out"
                  style={{ width: `${barPct}%` }}
                />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

// Accumulating result feed: generations stack newest-on-top. Each item is
// self-contained so actions and blur are per-item (work on older results too).
// Stays performant for hundreds of items via content-visibility, lazy images,
// and in-view-only video playback.
export default function GenerateFeed({
  feed = [],
  user,
  t,
  now,
  isDeleting,
  videoIsLoading,
  chatBusyId,
  charBusyId,
  onAnimate,
  onClone,
  onTweak,
  onChat,
  onUseCharacter,
  onDownload,
  onPublish,
  onSaveWorkflow,
  onDelete,
  onExtend,
  onRedo,
  onThumb,
  animateProgress = 0,
  animateQueueLength,
  animateExpectedDuration = 200,
  emptyState,
}) {
  const [fullSrc, setFullSrc] = useState(null)
  const openFull = useCallback((src) => setFullSrc(src), [])
  const [timerInfo, setTimerInfo] = useState(null)
  const openTimerInfo = useCallback(
    (remaining, isVideo, earlyAccess) =>
      setTimerInfo({ remaining, isVideo, earlyAccess }),
    [],
  )

  if (!feed.length) return emptyState || null
  // Stories of clips show as one card each, in place of their newest (src/lib/clip-chains.ts). No flag here: a clip
  // exists only if the backend delivered one (it decides, flag extend_clips), and a feed without clips is unchanged.
  // What a story's New scene can be animated from: the session's own pictures, newest first, never a paywalled one.
  const pictures = feed.filter(
    (it) => it.type === 'image' && !it.blurry && it.url,
  )
  const entries = foldClipChains(feed).map((e) =>
    e.kind === 'chain' ? { item: e.at, story: e.clips } : { item: e.item },
  )

  return (
    <div className="flex flex-col items-center gap-6 max-md:gap-0 w-full">
      {entries.map(({ item, story }) => (
        // PHONES (below md): each item is a full-viewport snap "page" (TikTok-style),
        // media centered in the band set by CreateSimplified's --feed-* vars, so exactly
        // one generation shows at a time. md+: just a row in the scroll column. Must
        // stay on the same breakpoint as the scroller's max-md:snap-* and the static
        // settings panel — a snap page inside a non-snapping scroller strands the feed.
        <div
          key={item.id}
          // Reveal anchor: CreateSimplified's scroll-up animation locates the
          // previously-top card by this id to know how far the new card pushed it down.
          data-feed-item-id={item.id}
          className="w-full max-md:flex max-md:flex-col max-md:items-center max-md:justify-center max-md:snap-start max-md:h-[var(--feed-page-height,100dvh)] max-md:pt-[var(--feed-top-reserve,72px)] max-md:pb-[var(--feed-bottom-reserve,140px)]"
        >
          {story && (
            <ClipChainCard
              clips={story}
              t={t}
              user={user}
              videoIsLoading={videoIsLoading}
              onExtend={onExtend}
              onRedo={onRedo}
              onDelete={onDelete}
              onDownload={onDownload}
              onPublish={onPublish}
              pictures={pictures}
              onAnimate={onAnimate}
            />
          )}
          {!story &&
            (item.type === 'pending' ? (
              <AnimatingCard
                source={item.source}
                sourceIsUrl={item.sourceIsUrl}
                videoProgress={animateProgress}
                queueLength={animateQueueLength}
                expectedDuration={animateExpectedDuration}
                t={t}
              />
            ) : item.type === 'image-pending' ? (
              <AnimatingCard
                expectedDuration={item.expectedDuration || 11}
                startedAt={item.startedAt}
                label={item.label || t('generating', 'Generating...')}
                secondaryLabel={item.secondaryLabel}
                phaseSwitchSeconds={item.phaseSwitchSeconds}
                t={t}
              />
            ) : item.type === 'tweak-pending' ? (
              <AnimatingCard
                source={item.source}
                sourceIsUrl={item.sourceIsUrl}
                expectedDuration={20}
                startedAt={item.startedAt}
                label={t('tweaking', 'Tweaking...')}
                t={t}
              />
            ) : (
              <FeedItem
                item={item}
                user={user}
                t={t}
                isDeleting={isDeleting}
                videoIsLoading={videoIsLoading}
                isChatting={chatBusyId === item.id}
                isCharBusy={charBusyId === item.id}
                onAnimate={onAnimate}
                onClone={onClone}
                onTweak={onTweak}
                onChat={onChat}
                onUseCharacter={onUseCharacter}
                onDownload={onDownload}
                onPublish={onPublish}
                onSaveWorkflow={onSaveWorkflow}
                onDelete={onDelete}
                onExtend={onExtend}
                onRedo={onRedo}
                onOpenFull={openFull}
                onTimerClick={openTimerInfo}
                onThumb={onThumb}
                countdown={
                  item.expiresAt
                    ? formatRemaining(item.expiresAt - (now || Date.now()))
                    : null
                }
              />
            ))}
        </div>
      ))}

      <ImageLightbox src={fullSrc} onClose={() => setFullSrc(null)} />

      <Modal
        isOpen={!!timerInfo}
        closeModal={() => setTimerInfo(null)}
        title={
          timerInfo?.earlyAccess
            ? t('eaTitle', 'Early access')
            : t('timerExpiryTitle', 'Save it before it expires')
        }
        content={
          timerInfo?.earlyAccess
            ? t('eaTimerBody', {
                defaultValue:
                  "This model is in early access, so its images can't be saved yet. This one disappears in about {{remaining}}.",
                remaining: timerInfo?.remaining || '',
              })
            : t('timerExpiryBody', {
                defaultValue:
                  "This {{type}} isn't saved yet. Save or publish it to keep it — otherwise it will disappear forever in about {{remaining}}.",
                type: timerInfo?.isVideo ? 'video' : 'image',
                remaining: timerInfo?.remaining || '',
              })
        }
        confirmation={t('gotIt', 'Got it')}
        onConfirm={() => setTimerInfo(null)}
      />
    </div>
  )
}
